import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  Bookmark,
  Briefcase,
  Building2,
  Check,
  Clock3,
  ChevronDown,
  ChevronUp,
  FileText,
  Filter,
  IndianRupee,
  Layers,
  Loader2,
  MapPin,
  Rocket,
  Search,
  LogOut,
  User as UserIcon,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import FeedbackHeaderCTA from './FeedbackHeaderCTA';
import AppSidebar, { APP_SIDEBAR_WIDTH } from './AppSidebar';
import CompanyPeoplePanel from './CompanyPeoplePanel';
import JobsEmptyState from './JobsEmptyState';
import DidYouApplyModal from './DidYouApplyModal';
import { AnimatePresence, motion } from 'framer-motion';
import { toast } from 'sonner';
import { getJobsFeedPage, getMailerDigestJobs, setJobStatus, getOrgMatchReasons } from '../api/insights';
import { appDesignTokens as tokens } from './appDesignTokens';
import { useTheme } from '../context/ThemeContext';
import ThemeToggle from './ThemeToggle';

const jobApplyKey = (job) => {
  const id = job?.job_id ?? job?.id;
  if (id != null && String(id).trim()) return `job:${id}`;
  const url = (job?.job_url || '').trim().toLowerCase();
  if (url) return `url:${url}`;
  const fallback = `${String(job?.role || '').trim().toLowerCase()}|${String(job?.company_name || '').trim().toLowerCase()}`;
  return fallback === '|' ? null : `meta:${fallback}`;
};

const formatPostedAgo = (postedAt) => {
  if (!postedAt) return '';
  const dt = new Date(postedAt);
  if (Number.isNaN(dt.getTime())) return '';
  const days = Math.floor((Date.now() - dt.getTime()) / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return '1d ago';
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
};

const formatExperience = (exp) => {
  if (exp == null || exp === '') return null;
  const n = Number(exp);
  if (!Number.isFinite(n)) return String(exp);
  if (n >= 8) return '8+ yrs';
  return `${n}+ yrs`;
};

const companyInitials = (name) => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

const buildPanelParamsFromJob = (job, { initialOuterTab = 'employees', meta = {} } = {}) => {
  const ctx = job?.panel_context || {};
  const sourceNames = meta.source_company_names || job.source_tags || [];
  return {
    sourceOrgId: ctx.source_org_id ?? (job.source_matched_org_ids?.[0] ?? null),
    sourceCompanyName: ctx.source_company_name || sourceNames[0] || '',
    destOrgId: ctx.dest_org_id ?? job.org_id ?? null,
    companyName: ctx.company_name || job.company_name || '',
    hop: ctx.hop || 1,
    role: ctx.role || '',
    location: '',
    initialOuterTab,
    initialSelectedJobId: initialOuterTab === 'jobs' ? (job.job_id ?? job.id) : null,
    initialSelectedJob: initialOuterTab === 'jobs' ? {
      ...job,
      id: job.job_id ?? job.id,
    } : null,
    isFromJobsPage: true,
  };
};
const SORT_OPTIONS = [
  { value: 'best_match', label: 'Best match' },
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
];
const HEADER_HEIGHT = 56;
const getViewportWidth = () => {
  if (typeof window === 'undefined') return 1024;
  return Math.min(
    window.innerWidth || 1024,
    window.visualViewport?.width || window.innerWidth || 1024
  );
};

const MailerJobsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, logout } = useAuth();
  const digest = useMemo(() => (searchParams.get('digest') || '').trim(), [searchParams]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [jobs, setJobs] = useState([]);
  const [meta, setMeta] = useState({});
  const [search, setSearch] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState('');
  const [sortBy, setSortBy] = useState('best_match');
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [locationMenuOpen, setLocationMenuOpen] = useState(false);
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  // Default ON so first-time/no-digest users see profile-matched jobs first.
  const [profileMatchOnly, setProfileMatchOnly] = useState(false);
  const [stagedLocations, setStagedLocations] = useState([]);
  const [activeFilters, setActiveFilters] = useState([]);
  const [resolvingFilterKey, setResolvingFilterKey] = useState('');
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);
  const [expandedJobKey, setExpandedJobKey] = useState(null);
  const [reasonsMap, setReasonsMap] = useState({});
  const [reasonsLoadingMap, setReasonsLoadingMap] = useState({});
  const [showAllReasonsMap, setShowAllReasonsMap] = useState({});
  const [panelCompany, setPanelCompany] = useState(null);
  const [feedExpired, setFeedExpired] = useState(false);
  const [allProcessed, setAllProcessed] = useState(false);
  const [profileIncomplete, setProfileIncomplete] = useState(false);
  const [watchedCompanies, setWatchedCompanies] = useState([]);
  const [statusCounts, setStatusCounts] = useState({ saved: 0, applied: 0, rejected: 0 });
  const [generatedAt, setGeneratedAt] = useState(null);
  const [lazyFilterOptions, setLazyFilterOptions] = useState({ core: [], people: [] });
  const [feedTotal, setFeedTotal] = useState(0);
  const [hasMoreJobs, setHasMoreJobs] = useState(false);
  const [nextOffset, setNextOffset] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasBootstrapped, setHasBootstrapped] = useState(false);
  const [filterResolution, setFilterResolution] = useState(null);
  const [pendingApplyJob, setPendingApplyJob] = useState(null);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [mobileLocationSearchOpen, setMobileLocationSearchOpen] = useState(false);
  const [mobileInfoTooltip, setMobileInfoTooltip] = useState('');
  const [isMobileLayout, setIsMobileLayout] = useState(() => getViewportWidth() <= 720);
  // "Get higher-chance matches" CTA state
  const [higherMatchesDismissed, setHigherMatchesDismissed] = useState(() => {
    try { return sessionStorage.getItem('hm_cta_dismissed') === '1'; } catch { return false; }
  });
  const [higherMatchesClicked, setHigherMatchesClicked] = useState(false);
  const [higherMatchesLoading, setHigherMatchesLoading] = useState(false);
  const higherMatchesTimeoutRef = useRef(null);
  const loadMoreRef = useRef(null);
  const profileMenuRef = useRef(null);
  const mainScrollRef = useRef(null);
  const appendInFlightRef = useRef(false);
  const requestedOffsetRef = useRef(new Set());
  const bootLoadingStartRef = useRef(null);
  const [bootLoadingSeconds, setBootLoadingSeconds] = useState(0);

  useEffect(() => {
    const update = () => setIsMobileLayout(getViewportWidth() <= 720);
    update();
    window.addEventListener('resize', update);
    window.visualViewport?.addEventListener('resize', update);
    return () => {
      window.removeEventListener('resize', update);
      window.visualViewport?.removeEventListener('resize', update);
    };
  }, []);

  useEffect(() => {
    const fn = (e) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setIsProfileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);
  const resolveJobId = (job) => {
    const id = job?.job_id ?? job?.id;
    if (id == null || String(id).trim() === '') return null;
    const n = Number(id);
    return Number.isFinite(n) ? n : null;
  };

  const loadJobsPage = useCallback(async ({ offset = 0, append = false, filters = activeFilters, searchVal = null, locationList = null } = {}) => {
    if (append) {
      if (appendInFlightRef.current) return;
      if (requestedOffsetRef.current.has(offset)) return;
      appendInFlightRef.current = true;
      requestedOffsetRef.current.add(offset);
    } else {
      requestedOffsetRef.current.clear();
    }
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setError('');
      setFeedExpired(false);
      setAllProcessed(false);
    }
    try {
      let data;
      if (digest) {
        const res = await getMailerDigestJobs({
          digestToken: digest,
          offset,
          limit: 10,
          search: (searchVal ?? submittedSearch).trim(),
          locations: locationList ?? selectedLocations,
          filters,
          profileMatchOnly,
          sortBy,
        });
        data = res.digest || {};
      } else {
        data = await getJobsFeedPage({
          offset,
          limit: 10,
          filters,
          search: (searchVal ?? submittedSearch).trim(),
          locations: locationList ?? selectedLocations,
          profileMatchOnly,
          sortBy,
        });
      }
      setFeedExpired(!!data.expired);
      setAllProcessed(!!data.all_processed);
      setProfileIncomplete(!!data.profile_incomplete);
      const pageJobs = Array.isArray(data.jobs) ? data.jobs : [];
      const next = data.next_offset != null ? Number(data.next_offset) : 0;
      const canLoadMore = !!data.has_more && pageJobs.length > 0 && Number.isFinite(next) && next > offset;
      setJobs((prev) => (append ? [...prev, ...pageJobs] : pageJobs));
      setMeta(data.meta || {});
      setLazyFilterOptions(data.lazy_filter_options || { core: [], people: [] });
      setGeneratedAt(data.generated_at || null);
      setWatchedCompanies(Array.isArray(data.watched_companies) ? data.watched_companies : []);
      setStatusCounts(data.status_counts || { saved: 0, applied: 0, rejected: 0 });
      setLocationOptions(Array.isArray(data.location_options) ? data.location_options : []);
      setFeedTotal(Number(data.total_unfiltered ?? data.total ?? data.jobs_total ?? 0));
      setHasMoreJobs(canLoadMore);
      setNextOffset(canLoadMore ? next : 0);
      setFilterResolution(data.filter_resolution || null);
    } catch (e) {
      if (append) {
        requestedOffsetRef.current.delete(offset);
      }
      setError(e?.message || 'Failed to load jobs.');
    } finally {
      if (append) {
        appendInFlightRef.current = false;
        setLoadingMore(false);
      } else {
        setLoading(false);
        setHasBootstrapped(true);
      }
    }
  }, [activeFilters, digest, submittedSearch, selectedLocations, profileMatchOnly, sortBy]);
  useEffect(() => {
    let mounted = true;
    const run = async () => {
      try {
        await loadJobsPage({ offset: 0, append: false, filters: activeFilters });
      } catch (e) {
        if (!mounted) return;
        const msg = e?.message || 'Failed to load jobs.';
        if (digest && /expired|410/i.test(msg)) {
          setFeedExpired(true);
          setJobs([]);
        } else {
          setError(msg);
        }
      }
    };
    run();
    return () => { mounted = false; };
  }, [digest, activeFilters, loadJobsPage]);
  useEffect(() => {
    if (!hasMoreJobs || loading || loadingMore) return undefined;
    const node = loadMoreRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        loadJobsPage({ offset: nextOffset, append: true, filters: activeFilters });
      }
    }, { rootMargin: '240px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [activeFilters, digest, hasMoreJobs, loadJobsPage, loading, loadingMore, nextOffset]);
  const removeJobFromFeed = useCallback((job) => {
    const jid = resolveJobId(job);
    setJobs((prev) => {
      const next = prev.filter((j) => resolveJobId(j) !== jid);
      if (next.length === 0) setAllProcessed(true);
      return next;
    });
  }, []);
  const persistStatus = useCallback(async (job, status) => {
    const jid = resolveJobId(job);
    if (!jid) return;
    await setJobStatus(jid, status);
  }, []);
  const openFindContact = useCallback((job) => {
    setPanelCompany({
      ...buildPanelParamsFromJob(job, { initialOuterTab: 'employees', meta }),
      role: '',
    });
  }, [meta]);
  const openViewDetails = useCallback((job) => {
    setPanelCompany(buildPanelParamsFromJob(job, { initialOuterTab: 'jobs', meta }));
  }, [meta]);
  const toggleFilter = (filter) => {
    const key = filter?.key;
    if (!key) return;
    if (mainScrollRef.current) {
      mainScrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
    if (activeFilters.some((f) => f.key === key)) {
      setActiveFilters((prev) => prev.filter((f) => f.key !== key));
      return;
    }
    setResolvingFilterKey(key);
    setActiveFilters((prev) => (
      prev.some((f) => f.key === key) ? prev : [...prev, filter]
    ));
    setTimeout(() => setResolvingFilterKey(''), 300);
  };

  const [locSearch, setLocSearch] = useState('');
  const locationsWithCounts = useMemo(
    () => (locationOptions || []).map((x) => ({ loc: String(x.loc || ''), count: Number(x.count || 0) })),
    [locationOptions]
  );
  const filteredLocations = useMemo(() => {
    if (!locSearch) return locationsWithCounts;
    return locationsWithCounts.filter((l) =>
      l.loc.toLowerCase().includes(locSearch.toLowerCase())
    );
  }, [locationsWithCounts, locSearch]);
  const toggleStagedLocation = (loc) => {
    setStagedLocations((prev) => (prev.includes(loc) ? prev.filter((l) => l !== loc) : [...prev, loc]));
  };
  const applyLocationFilter = () => {
    setSelectedLocations(stagedLocations);
    setLocationMenuOpen(false);
  };
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      const nextSearch = search.trim();
      if (nextSearch === submittedSearch.trim()) {
        loadJobsPage({ offset: 0, append: false, searchVal: nextSearch });
        return;
      }
      setSubmittedSearch(nextSearch);
    }
  };
  const savedCount = statusCounts.saved || 0;
  const appliedCount = statusCounts.applied || 0;
  const showBlockingLoader = Boolean(loading && !hasBootstrapped);
  useEffect(() => {
    if (!showBlockingLoader) {
      bootLoadingStartRef.current = null;
      setBootLoadingSeconds(0);
      return undefined;
    }
    if (!bootLoadingStartRef.current) {
      bootLoadingStartRef.current = Date.now();
    }
    const tick = () => {
      const startedAt = bootLoadingStartRef.current || Date.now();
      setBootLoadingSeconds(Math.max(0, Math.floor((Date.now() - startedAt) / 1000)));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [showBlockingLoader]);
  const showEmptyState = !loading && !error && (feedExpired || allProcessed);
  const hasProfileSignals = useMemo(() => {
    const openRoles = Array.isArray(meta?.open_roles) ? meta.open_roles.filter(Boolean) : [];
    const sources = Array.isArray(meta?.source_company_names) ? meta.source_company_names.filter(Boolean) : [];
    return openRoles.length > 0 || sources.length > 0;
  }, [meta]);
  const emptyVariant = profileIncomplete
    ? 'profile_needed'
    : (!hasProfileSignals ? 'profile_needed' : (feedExpired ? 'expired' : 'all_processed'));
  const filteredJobs = useMemo(() => {
    return jobs;
  }, [jobs]);
  const openRolesCount = Number.isFinite(Number(feedTotal)) ? Number(feedTotal) : filteredJobs.length;
  const companyCount = useMemo(() => new Set(filteredJobs.map((j) => j.company_name)).size, [filteredJobs]);
  const isComputingFilters = Boolean(loading && activeFilters.length > 0);
  const shouldShowWhyMatches = profileMatchOnly || activeFilters.length > 0;
  const activeFilterChips = useMemo(() => {
    const chips = [];
    if (selectedLocations.length) chips.push({ key: 'locations', label: `${selectedLocations.length} locations` });
    const sortLabel = SORT_OPTIONS.find((o) => o.value === sortBy)?.label;
    if (sortLabel) chips.push({ key: 'sort', label: sortLabel });
    activeFilters.forEach((f) => chips.push({ key: f.key, label: f.label, filter: f }));
    return chips;
  }, [activeFilters, selectedLocations.length, sortBy]);
  const mobileLocationOptions = useMemo(() => {
    const selectedSet = new Set(selectedLocations);
    const selected = selectedLocations
      .map((loc) => locationsWithCounts.find((x) => x.loc === loc) || { loc, count: 0 })
      .filter((item) => item.loc);
    const topByCount = [...locationsWithCounts]
      .filter((item) => item.loc && !selectedSet.has(item.loc))
      .sort((a, b) => b.count - a.count)
      .slice(0, Math.max(0, 3 - selected.length));
    return [...selected, ...topByCount].slice(0, 3);
  }, [locationsWithCounts, selectedLocations]);
  const mobileLocationSearchResults = useMemo(() => {
    const q = locSearch.trim().toLowerCase();
    if (!q) return [];
    return locationsWithCounts
      .filter((item) => item.loc && item.loc.toLowerCase().includes(q))
      .sort((a, b) => {
        const aSelected = selectedLocations.includes(a.loc) ? 1 : 0;
        const bSelected = selectedLocations.includes(b.loc) ? 1 : 0;
        return bSelected - aSelected || b.count - a.count;
      })
      .slice(0, 8);
  }, [locationsWithCounts, locSearch, selectedLocations]);
  const toggleMobileLocation = (loc) => {
    setSelectedLocations((prev) => (prev.includes(loc) ? prev.filter((l) => l !== loc) : [...prev, loc]));
  };
  const resetMobileFilters = () => {
    setProfileMatchOnly(true);
    setSelectedLocations([]);
    setSortBy('best_match');
    setActiveFilters([]);
    setMoreFiltersOpen(false);
    setMobileLocationSearchOpen(false);
    setLocSearch('');
  };
  const bootLoadingStatus = useMemo(() => {
    if (bootLoadingSeconds < 4) {
      return {
        title: 'Preparing your jobs feed',
        detail: 'Checking your preferences and profile signals.',
        eta: 'Usually under 10 seconds',
      };
    }
    if (bootLoadingSeconds < 10) {
      return {
        title: 'Matching jobs to your profile',
        detail: 'Filtering jobs by your role, experience, and quality signals.',
        eta: 'Should complete shortly',
      };
    }
    return {
      title: 'Running deeper matching',
      detail: 'This can take a bit longer. Be assured, we are fetching the best and most recent jobs.',
      eta: 'Can take up to 30-40 seconds',
    };
  }, [bootLoadingSeconds]);
  const noFilterDataMessage = useMemo(() => {
    const unresolved = Array.isArray(filterResolution?.unresolved_filters)
      ? filterResolution.unresolved_filters.filter(Boolean)
      : [];
    if (filterResolution?.all_unresolved && unresolved.length > 0) {
      if (unresolved.length === 1) {
        return `We do not have enough data for "${unresolved[0]}" yet. Try another filter or update your profile history.`;
      }
      return 'We do not have enough data for the selected filters yet. Try another filter or update your profile history.';
    }
    return 'No jobs match your filters.';
  }, [filterResolution]);
  const toggleSaved = useCallback(async (job) => {
    const jid = resolveJobId(job);
    if (!jid || job.user_status === 'saved') return;
    try {
      await persistStatus(job, 'saved');
      setJobs((prev) => prev.map((j) => (
        resolveJobId(j) === jid ? { ...j, user_status: 'saved' } : j
      )));
      setStatusCounts((prev) => ({ ...prev, saved: (prev.saved || 0) + 1 }));
    } catch (e) {
      setError(e?.message || 'Could not save job.');
    }
  }, [persistStatus]);
  const recordApply = useCallback(async (job) => {
    const jid = resolveJobId(job);
    if (!jid) return;
    try {
      await persistStatus(job, 'applied');
      setStatusCounts((prev) => ({ ...prev, applied: (prev.applied || 0) + 1 }));
      removeJobFromFeed(job);
    } catch (e) {
      setError(e?.message || 'Could not mark as applied.');
    }
  }, [persistStatus, removeJobFromFeed]);

  const handleApplyClick = useCallback((job) => {
    setPendingApplyJob(job);
    const handleFocus = () => {
      // Delay slightly to ensure tab has fully regained focus
      setTimeout(() => setShowApplyModal(true), 300);
      window.removeEventListener('focus', handleFocus);
    };
    window.addEventListener('focus', handleFocus);
  }, []);

  const handleApplyConfirm = async (didApply) => {
    if (!pendingApplyJob) return;
    const job = pendingApplyJob;
    setShowApplyModal(false);
    setPendingApplyJob(null);
    if (didApply) {
      await recordApply(job);
    }
  };
  const rejectJob = useCallback(async (job) => {
    const jid = resolveJobId(job);
    if (!jid) return;
    try {
      await persistStatus(job, 'rejected');
      setStatusCounts((prev) => ({ ...prev, rejected: (prev.rejected || 0) + 1 }));
      removeJobFromFeed(job);
    } catch (e) {
      setError(e?.message || 'Could not dismiss job.');
    }
  }, [persistStatus, removeJobFromFeed]);
  const primaryRole = meta.primary_role || 'Software Engineer';
  const heroLabel = meta.hero_label || `CURATED FOR ${(meta.user_name || 'YOU').toUpperCase()}`;
  const { isDark } = useTheme();
  const pageBg = isDark ? '#0d1019' : tokens.colors.appShell;
  const cardBg = isDark ? 'rgba(255,255,255,0.04)' : tokens.colors.surface;
  const border = isDark ? 'rgba(255,255,255,0.08)' : tokens.colors.border;
  const text = isDark ? 'rgba(255,255,255,0.9)' : tokens.colors.textPrimary;
  const muted = isDark ? 'rgba(255,255,255,0.45)' : tokens.colors.textMuted;
  const green = isDark ? '#34d399' : tokens.colors.success;
  const headerBg = isDark ? 'rgba(13,16,25,0.92)' : tokens.colors.appShell;
  const menuBg = isDark ? '#0d1019' : tokens.colors.surface;
  const accentColor = isDark ? '#F59E0B' : tokens.colors.accent;

  // Handler for "Get higher-chance matches" CTA
  const handleHigherMatchesClick = useCallback(async () => {
    if (higherMatchesClicked) return;
    setHigherMatchesClicked(true);
    setHigherMatchesLoading(true);
    try { sessionStorage.setItem('hm_cta_dismissed', '1'); } catch { }
    setHigherMatchesDismissed(true);

    // Build full filter set: turn on all core + people filters, profileMatchOnly = true
    const allCoreFilters = lazyFilterOptions.core || [];
    const allPeopleFilters = lazyFilterOptions.people || [];
    const allFilters = [...allCoreFilters, ...allPeopleFilters];

    // Set up a 30-second timeout
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('TIMEOUT')), 180000)
    );

    // Clear the timeout ref in case it's already set
    if (higherMatchesTimeoutRef.current) clearTimeout(higherMatchesTimeoutRef.current);

    try {
      await Promise.race([
        new Promise((resolve, reject) => {
          // We trigger load directly rather than via state to avoid async batching issues
          (async () => {
            try {
              let data;
              if (digest) {
                const res = await getMailerDigestJobs({
                  digestToken: digest,
                  offset: 0,
                  limit: 10,
                  search: submittedSearch.trim(),
                  locations: selectedLocations,
                  filters: allFilters,
                  profileMatchOnly: true,
                  sortBy,
                });
                data = res.digest || {};
              } else {
                data = await getJobsFeedPage({
                  offset: 0,
                  limit: 10,
                  filters: allFilters,
                  search: submittedSearch.trim(),
                  locations: selectedLocations,
                  profileMatchOnly: true,
                  sortBy,
                });
              }
              setFeedExpired(!!data.expired);
              setAllProcessed(!!data.all_processed);
              setProfileIncomplete(!!data.profile_incomplete);
              const pageJobs = Array.isArray(data.jobs) ? data.jobs : [];
              const next = data.next_offset != null ? Number(data.next_offset) : 0;
              const canLoadMore = !!data.has_more && pageJobs.length > 0 && Number.isFinite(next) && next > 0;
              setJobs(pageJobs);
              setMeta(data.meta || {});
              setLazyFilterOptions(data.lazy_filter_options || { core: [], people: [] });
              setGeneratedAt(data.generated_at || null);
              setWatchedCompanies(Array.isArray(data.watched_companies) ? data.watched_companies : []);
              setStatusCounts(data.status_counts || { saved: 0, applied: 0, rejected: 0 });
              setLocationOptions(Array.isArray(data.location_options) ? data.location_options : []);
              setFeedTotal(Number(data.total_unfiltered ?? data.total ?? data.jobs_total ?? 0));
              setHasMoreJobs(canLoadMore);
              setNextOffset(canLoadMore ? next : 0);
              setFilterResolution(data.filter_resolution || null);
              setHasBootstrapped(true);
              setProfileMatchOnly(true);
              setActiveFilters(allFilters);
              resolve();
            } catch (err) {
              reject(err);
            }
          })();
        }),
        timeoutPromise,
      ]);

      // Success: scroll to top
      setHigherMatchesLoading(false);
      if (mainScrollRef.current) {
        mainScrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (err) {
      setHigherMatchesLoading(false);
      // On timeout or error: fall back and show error
      if (err?.message === 'TIMEOUT') {
        toast.error('Could not load higher-chance matches in time. Showing available matches.');
      } else {
        toast.error('Failed to load higher-chance matches. Please try again.');
      }
      if (mainScrollRef.current) {
        mainScrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  }, [digest, higherMatchesClicked, lazyFilterOptions, selectedLocations, sortBy, submittedSearch]);
  const mobileCoreFilters = lazyFilterOptions.core || [];
  const mobilePeopleFilters = (lazyFilterOptions.people || []).slice(0, 6);
  const mobileChipStyle = (active, wide = false) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: wide ? 'flex-start' : 'center',
    minHeight: 36,
    maxWidth: '100%',
    border: `1px solid ${active ? accentColor : border}`,
    borderRadius: 999,
    background: active ? accentColor : (isDark ? 'rgba(255,255,255,0.05)' : tokens.colors.surfaceSubtle),
    color: active ? (isDark ? '#1a1612' : '#fff') : text,
    padding: '7px 12px',
    fontSize: 12.5,
    lineHeight: 1.25,
    fontWeight: 750,
    cursor: 'pointer',
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    textAlign: wide ? 'left' : 'center',
  });
  const mobileSectionStyle = { padding: '0 18px', marginBottom: 18 };
  const mobileSectionLabelStyle = {
    marginBottom: 9,
    color: muted,
    fontSize: 10,
    fontWeight: 900,
    letterSpacing: '0.09em',
    textTransform: 'uppercase',
  };
  const mobileInfoButtonStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    borderRadius: 999,
    border: `1px solid ${border}`,
    background: cardBg,
    color: muted,
    cursor: 'help',
    padding: 0,
  };
  const mobileWrapStyle = { display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' };
  const renderMobileInfoButton = (id, text) => (
    <span style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button"
        aria-label={text}
        onMouseEnter={() => setMobileInfoTooltip(id)}
        onMouseLeave={() => setMobileInfoTooltip('')}
        onFocus={() => setMobileInfoTooltip(id)}
        onBlur={() => setMobileInfoTooltip('')}
        onClick={(e) => {
          e.preventDefault();
          setMobileInfoTooltip((current) => (current === id ? '' : id));
        }}
        style={mobileInfoButtonStyle}
      >
        <span style={{ fontSize: 12, fontWeight: 900, lineHeight: 1 }}>i</span>
      </button>
      {mobileInfoTooltip === id && (
        <span
          style={{
            position: 'absolute',
            left: 'calc(100% + 8px)',
            top: '50%',
            transform: 'translateY(-50%)',
            width: 230,
            maxWidth: '62vw',
            borderRadius: 12,
            background: tokens.colors.surface,
            color: tokens.colors.textSecondary,
            border: `1px solid ${tokens.colors.border}`,
            padding: '9px 11px',
            fontSize: 11.5,
            fontWeight: 700,
            lineHeight: 1.35,
            boxShadow: tokens.shadow.menu,
            zIndex: 110,
            pointerEvents: 'none',
            textTransform: 'none',
            letterSpacing: 0,
          }}
        >
          {text}
        </span>
      )}
    </span>
  );
  return (
    <div className={`compact-ui ${isMobileLayout ? 'jobs-mobile-layout' : ''}`} style={{ minHeight: '100vh', background: pageBg, display: 'flex', flexDirection: 'column', fontFamily: tokens.fonts.body }}>
      <header style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 30,
        height: HEADER_HEIGHT,
        padding: '0 16px 0 0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: headerBg,
        borderBottom: `1px solid ${border}`,
        backdropFilter: 'blur(16px)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <button
            type="button"
            onClick={() => navigate(-1)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              border: 'none',
              background: 'transparent',
              color: muted,
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 500,
              height: HEADER_HEIGHT,
              padding: '0 16px',
            }}
          >
            <ArrowLeft size={15} />
            <span>Back</span>
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }} onClick={() => navigate('/')}>
            <div style={{
              width: 32,
              height: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: cardBg,
              borderRadius: tokens.radius.sm,
              padding: 4,
              border: `1px solid ${border}`,
            }}>
              <img src="/assets/logo.png" alt="fairhire logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </div>
            <span className="app-header-brand" style={{ fontSize: 16, fontWeight: 800, fontFamily: tokens.fonts.heading, color: text }}>fairhire</span>
          </div>
        </div>
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 12 }} ref={profileMenuRef}>
          <FeedbackHeaderCTA />
          <ThemeToggle />
          <button
            type="button"
            onClick={() => setIsProfileMenuOpen((v) => !v)}
            style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              background: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(37,99,235,0.1)',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(37,99,235,0.2)'}`,
              fontWeight: 800,
              fontSize: 12,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <UserIcon size={15} color={accentColor} />
          </button>
          <AnimatePresence>
            {isProfileMenuOpen && (
              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                style={{
                  position: 'absolute',
                  right: 0,
                  top: 'calc(100% + 8px)',
                  width: 200,
                  background: menuBg,
                  border: `1px solid ${border}`,
                  borderRadius: tokens.radius.card,
                  padding: 8,
                  boxShadow: isDark ? '0 12px 36px rgba(0,0,0,0.6)' : tokens.shadow.menu,
                }}
              >
                <div style={{ padding: '8px 12px', borderBottom: `1px solid ${border}`, marginBottom: 4 }}>
                  <p style={{ fontSize: 11, color: muted, marginBottom: 2 }}>Signed in as</p>
                  <p style={{ fontSize: 13, fontWeight: 600, color: text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.name || user?.email || 'User'}</p>
                </div>
                {[
                  { label: 'Profile', icon: <UserIcon size={14} />, onClick: () => { setIsProfileMenuOpen(false); navigate('/profile'); }, color: isDark ? 'rgba(255,255,255,0.75)' : tokens.colors.textSecondary },
                  { label: 'Logout', icon: <LogOut size={14} />, onClick: () => { logout(); setIsProfileMenuOpen(false); navigate('/'); toast.success('Logged out'); }, color: tokens.colors.danger },
                ].map((item) => (
                  <button
                    key={item.label}
                    onClick={item.onClick}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '8px 12px',
                      borderRadius: 8,
                      background: 'none',
                      border: 'none',
                      color: item.color,
                      fontSize: 13,
                      cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(37,99,235,0.08)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                  >
                    {item.icon}{item.label}
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </header>
      <div style={{ flex: 1, minHeight: 0, display: 'flex', marginTop: HEADER_HEIGHT }}>
        <AppSidebar width={APP_SIDEBAR_WIDTH} />
        <main
          className="jobs-main-scroll"
          ref={mainScrollRef}
          style={{
            flex: 1,
            overflow: showBlockingLoader ? 'hidden' : 'auto',
            width: isMobileLayout ? '100%' : undefined,
            height: isMobileLayout ? `calc(100vh - ${HEADER_HEIGHT}px - 74px - env(safe-area-inset-bottom))` : `calc(100vh - ${HEADER_HEIGHT}px)`,
            paddingBottom: isMobileLayout ? 'calc(74px + env(safe-area-inset-bottom))' : undefined,
          }}
        >
          <div className="jobs-shell-content" style={{ padding: isMobileLayout ? '14px 12px 96px' : '20px 28px 40px', maxWidth: 940, margin: '0 auto' }}>
            {error && (
              <div style={{ padding: 12, borderRadius: 12, background: 'rgba(239,68,68,0.08)', color: tokens.colors.danger, display: 'flex', gap: 8, marginBottom: 16 }}>
                <AlertCircle size={16} /> {error}
              </div>
            )}
            {showBlockingLoader ? (
              <div
                style={{
                  minHeight: 'calc(100vh - 180px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '20px 12px',
                }}
              >
                <div style={{
                  width: '100%',
                  maxWidth: 460,
                  borderRadius: 16,
                  border: `1px solid ${border}`,
                  background: cardBg,
                  boxShadow: isDark ? 'none' : tokens.shadow.card,
                  padding: '20px 18px',
                  textAlign: 'center',
                }}>
                  <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 48, height: 48, borderRadius: 12, background: isDark ? 'rgba(245,158,11,0.12)' : 'rgba(37,99,235,0.08)', color: accentColor, marginBottom: 12 }}>
                    <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
                  </div>
                  <div style={{ fontSize: 17, fontWeight: 800, color: text, marginBottom: 6 }}>
                    {bootLoadingStatus.title}
                  </div>
                  <div style={{ fontSize: 13.5, lineHeight: 1.55, color: isDark ? 'rgba(255,255,255,0.6)' : tokens.colors.textSecondary, marginBottom: 10 }}>
                    {bootLoadingStatus.detail}
                  </div>
                  <div style={{ fontSize: 12.5, color: muted, marginBottom: 10 }}>
                    {bootLoadingStatus.eta}
                  </div>
                  <div style={{ width: '100%', height: 6, borderRadius: 999, background: isDark ? 'rgba(245,158,11,0.15)' : 'rgba(37,99,235,0.12)', overflow: 'hidden' }}>
                    <div style={{
                      width: `${Math.min(92, 18 + (bootLoadingSeconds * 6))}%`,
                      height: '100%',
                      background: 'linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)',
                      transition: 'width 0.8s ease',
                    }} />
                  </div>
                  <div style={{ fontSize: 11.5, color: tokens.colors.textMuted, marginTop: 8 }}>
                    {bootLoadingSeconds < 60 ? `${bootLoadingSeconds}` : `${Math.floor(bootLoadingSeconds / 60)}m ${bootLoadingSeconds % 60}`}
                  </div>
                </div>
              </div>
            ) : showEmptyState ? (
              <JobsEmptyState
                userName={user?.name || meta.user_name}
                variant={emptyVariant}
                watchedCompanies={watchedCompanies}
                lastSweptAt={generatedAt}
              />
            ) : (
              <>
                <section className="jobs-hero-section" style={{ marginBottom: 20 }}>
                  <div className="jobs-hero-label" style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.15em', color: accentColor, marginBottom: 8, textTransform: 'uppercase' }}>
                    {heroLabel}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
                    {/* <div style={{ flex: 1, minWidth: 260 }}>
                      <h1 style={{ margin: 0, fontSize: 34, lineHeight: 1.15, fontWeight: 800, color: text, fontFamily: tokens.fonts.heading }}>
                        {primaryRole} roles,{' '}
                        <span style={{ color: tokens.colors.accent }}>matched to your path</span>
                      </h1>
                      <p className="jobs-hero-subtitle" style={{ margin: '10px 0 0', color: muted, fontSize: 14, maxWidth: 560 }}>
                        Find jobs matching your skills at companies proven to hire people like you. Sorted by best match.
                      </p>
                    </div> */}
                    <div style={{ flex: 1, minWidth: 260 }}>
                      <h1 style={{ margin: 0, fontSize: 32, lineHeight: 1.15, fontWeight: 800, color: text, fontFamily: tokens.fonts.heading }}>
                        Roles matched
                        <span style={{ color: accentColor }}> to your path</span>
                      </h1>
                      <p className="jobs-hero-subtitle" style={{ margin: '12px 0 0', color: muted, fontSize: 14, maxWidth: 560 }}>
                        Jobs matching your skills across all your target roles, at companies proven to hire people like you.
                      </p>
                    </div>
                    <div className="jobs-role-badge-wrap" style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
                      <div style={{
                        padding: '6px 14px', borderRadius: tokens.radius.md,
                        background: isDark ? 'rgba(255,255,255,0.04)' : tokens.colors.surfaceInset,
                        border: `1px solid ${border}`,
                        display: 'flex', alignItems: 'center', gap: 6,
                        boxShadow: isDark ? 'none' : tokens.shadow.card,
                      }}>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                          <span style={{ fontSize: 15, fontWeight: 800, color: text }}>{openRolesCount}</span>
                          <span style={{ fontSize: 11, fontWeight: 700, color: muted, textTransform: 'uppercase', letterSpacing: '0.02em' }}>Open Roles</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
                <div
                  style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 12,
                    background: pageBg,
                    paddingTop: 8,
                    marginBottom: 12,
                    borderBottom: `1px solid ${border}`,
                  }}
                >
                  <section style={{ padding: '0', marginBottom: 16 }}>
                    <div className="jobs-filter-row" style={{ display: 'flex', alignItems: 'stretch', gap: 12, marginBottom: 20 }}>
                      <div style={{ flex: 1, position: 'relative' }}>
                        <Search size={18} color={muted} style={{ position: 'absolute', left: 14, top: 14 }} />
                        <input
                          className="jobs-search-input"
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          onKeyDown={handleSearchKeyDown}
                          placeholder="Search company or role..."
                          style={{
                            width: '100%', padding: '12px 154px 12px 44px', borderRadius: 12,
                            border: `1px solid ${border}`, fontSize: 15,
                            background: isDark ? 'rgba(255,255,255,0.05)' : tokens.colors.surface,
                            color: text,
                            outline: 'none',
                          }}
                        />
                        <span
                          className="jobs-search-hint"
                          style={{
                            position: 'absolute',
                            right: 14,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: search.trim() !== submittedSearch.trim() ? tokens.colors.accent : muted,
                            fontSize: 12,
                            fontWeight: 700,
                            pointerEvents: 'none',
                            whiteSpace: 'nowrap',
                          }}
                        >
                        </span>
                      </div>
                      <button
                        type="button"
                        className="jobs-mobile-filter-button"
                        onClick={() => {
                          setMobileFiltersOpen(true);
                          setStagedLocations(selectedLocations);
                        }}
                        style={{
                          display: isMobileLayout ? 'inline-flex' : 'none',
                          alignItems: 'center',
                          gap: 6,
                          padding: '12px 14px',
                          borderRadius: 12,
                           border: `1px solid ${activeFilterChips.length ? accentColor : border}`,
                           background: activeFilterChips.length ? (isDark ? 'rgba(245,158,11,0.1)' : 'rgba(37,99,235,0.08)') : cardBg,
                           color: activeFilterChips.length ? accentColor : text,
                          fontSize: 13,
                          fontWeight: 800,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <Filter size={15} />
                        Filters
                      </button>
                      <div className="jobs-desktop-filter-controls" style={{ display: isMobileLayout ? 'none' : 'contents' }}>
                        <DropdownButton
                          label={
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <MapPin size={16} color={muted} />
                              <span>{selectedLocations.length ? `${selectedLocations.length} locations` : 'Location'}</span>
                            </div>
                          }
                          open={locationMenuOpen}
                          onToggle={() => {
                            const willOpen = !locationMenuOpen;
                            setLocationMenuOpen(willOpen);
                            setSortMenuOpen(false);
                            if (willOpen) setStagedLocations(selectedLocations);
                          }}
                          isDark={isDark}
                          border={border}
                          text={text}
                          noPadding
                        >
                          <div
                            style={{
                              minWidth: 'min(280px, calc(100vw - 32px))',
                              width: 'min(320px, calc(100vw - 32px))',
                              maxHeight: 360,
                              display: 'flex',
                              flexDirection: 'column',
                              overflow: 'hidden',
                            }}
                          >
                            <div style={{ padding: 8, borderBottom: `1px solid ${border}`, marginBottom: 4 }}>
                              <div style={{ position: 'relative' }}>
                                <Search size={12} color={muted} style={{ position: 'absolute', left: 8, top: 8 }} />
                                <input
                                  value={locSearch}
                                  onChange={(e) => setLocSearch(e.target.value)}
                                  placeholder="Search locations..."
                                  style={{
                                    width: '100%', padding: '6px 8px 6px 26px', borderRadius: 6,
                                    border: `1px solid ${border}`, fontSize: 12,
                                    background: isDark ? 'rgba(255,255,255,0.05)' : undefined,
                                    color: text,
                                  }}
                                />
                              </div>
                            </div>
                            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                              {filteredLocations.map(({ loc, count }) => (
                                <label key={loc} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '8px 10px', fontSize: 13, cursor: 'pointer', borderRadius: 6 }} onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : tokens.colors.surfaceInset} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <input type="checkbox" checked={stagedLocations.includes(loc)} onChange={() => toggleStagedLocation(loc)} />
                                    <span style={{ color: text }}>{loc}</span>
                                  </div>
                                  <span style={{ fontSize: 11, fontWeight: 700, color: muted, background: isDark ? 'rgba(255,255,255,0.06)' : tokens.colors.surfaceSubtle, padding: '2px 6px', borderRadius: 4 }}>{count}</span>
                                </label>
                              ))}
                            </div>
                            <div style={{ padding: 8, borderTop: `1px solid ${border}`, marginTop: 4, display: 'flex', justifyContent: 'flex-end', background: isDark ? '#0d1019' : tokens.colors.surface }}>
                              <button
                                type="button"
                                onClick={applyLocationFilter}
                                style={{
                                  padding: '6px 16px', borderRadius: 8,
                                  background: isDark ? 'linear-gradient(135deg,#F59E0B,#D97706)' : tokens.colors.accent,
                                  color: isDark ? '#1a1612' : '#fff',
                                  border: 'none', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                                }}
                              >
                                Apply
                              </button>
                            </div>
                          </div>
                        </DropdownButton>
                        <DropdownButton
                          label={SORT_OPTIONS.find((o) => o.value === sortBy)?.label || 'Best match'}
                          open={sortMenuOpen}
                          onToggle={() => { setSortMenuOpen((v) => !v); setLocationMenuOpen(false); }}
                          isDark={isDark}
                          border={border}
                          text={text}
                        >
                          {SORT_OPTIONS.map((opt) => (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => { setSortBy(opt.value); setSortMenuOpen(false); }}
                              style={{
                                display: 'block', width: '100%', textAlign: 'left', padding: '8px 4px',
                                border: 'none',
                                background: sortBy === opt.value ? (isDark ? 'rgba(245,158,11,0.1)' : 'rgba(37,99,235,0.08)') : 'transparent',
                                fontSize: 13, cursor: 'pointer', borderRadius: 6,
                                color: sortBy === opt.value ? accentColor : (isDark ? 'rgba(255,255,255,0.7)' : tokens.colors.textSecondary),
                              }}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </DropdownButton>
                      </div>
                    </div>
                    <div className="jobs-mobile-active-filters" style={{ display: 'none' }}>
                      {activeFilterChips.map((chip) => (
                        <button
                          key={chip.key}
                          type="button"
                          onClick={() => {
                            if (chip.key === 'locations') setSelectedLocations([]);
                            else if (chip.key === 'sort') setSortBy('best_match');
                            else if (chip.filter) toggleFilter(chip.filter);
                          }}
                        >
                          {chip.label}
                          <X size={11} />
                        </button>
                      ))}
                    </div>
                    <div className="jobs-profile-match-row" style={{ display: isMobileLayout ? 'none' : 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                      <div
                        onClick={() => setProfileMatchOnly(!profileMatchOnly)}
                        style={{
                          width: 38, height: 20, borderRadius: 20,
                          background: profileMatchOnly ? accentColor : (isDark ? 'rgba(255,255,255,0.15)' : tokens.colors.border),
                          position: 'relative', cursor: 'pointer', transition: 'all 0.2s', flexShrink: 0
                        }}
                      >
                        <div style={{
                          width: 14, height: 14, borderRadius: '50%', background: '#fff',
                          position: 'absolute', top: 3, left: profileMatchOnly ? 21 : 3,
                          transition: 'all 0.2s',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                        }} />
                      </div>
                      <span style={{ fontSize: 14, color: text }}>
                        Show only <strong>{primaryRole}</strong> roles matching your profile
                      </span>
                    </div>
                    <div className="jobs-company-filter-label" style={{ display: isMobileLayout ? 'none' : undefined, fontSize: 13, color: muted, marginBottom: 8 }}>Show jobs at companies that </div>
                    {!hasProfileSignals && (
                      <div
                        style={{
                          marginBottom: 12,
                          padding: '12px 14px',
                          borderRadius: 12,
                          border: `1px solid ${border}`,
                          background: cardBg,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 12,
                          flexWrap: 'wrap',
                        }}
                      >
                        <div style={{ minWidth: 220 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: text, marginBottom: 2 }}>
                            Complete your profile for better matches
                          </div>
                          <div style={{ fontSize: 12.5, color: muted }}>
                            Complete your profile to see jobs at companies that hired your past college and work alumni to make job search faster
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => navigate('/profile')}
                          style={{
                            padding: '8px 12px',
                            borderRadius: 8,
                            border: 'none',
                            background: tokens.colors.accent,
                            color: '#fff',
                            fontSize: 12.5,
                            fontWeight: 700,
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          Complete profile
                        </button>
                      </div>
                    )}
                    <div className="jobs-company-filter-chips" style={{ display: isMobileLayout ? 'none' : 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                      {(lazyFilterOptions.core || []).map((f) => (
                        <FilterChip
                          key={f.key}
                          active={activeFilters.some((x) => x.key === f.key)}
                          onClick={() => toggleFilter(f)}
                          label={resolvingFilterKey === f.key ? 'Checking...' : f.label}
                          isDark={isDark}
                          border={border}
                          accentColor={accentColor}
                        />
                      ))}
                      {(lazyFilterOptions.people || []).length > 0 && (
                        <button
                          type="button"
                          onClick={() => setMoreFiltersOpen((v) => !v)}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px',
                            borderRadius: tokens.radius.pill,
                            border: `1px solid ${moreFiltersOpen ? (isDark ? 'rgba(245,158,11,0.3)' : 'rgba(37,99,235,0.24)') : border}`,
                            background: moreFiltersOpen ? (isDark ? 'rgba(245,158,11,0.1)' : 'rgba(37,99,235,0.08)') : cardBg,
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: 'pointer',
                            color: moreFiltersOpen ? accentColor : text,
                          }}
                        >
                          <Filter size={14} /> Find Referrals
                        </button>
                      )}
                    </div>
                    {isComputingFilters && (
                      <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 8, border: `1px solid ${border}`, background: isDark ? 'rgba(255,255,255,0.04)' : tokens.colors.surfaceSubtle, fontSize: 12.5, color: isDark ? 'rgba(255,255,255,0.6)' : tokens.colors.textSecondary, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Loader2 size={13} className="spin" />
                        Computing hiring signals across your profile. Finding companies proven to hire people like you.
                      </div>
                    )}
                    {moreFiltersOpen && (
                      <div style={{ marginTop: 14, padding: 14, border: `1px solid ${border}`, borderRadius: tokens.radius.lg, background: isDark ? 'rgba(255,255,255,0.02)' : tokens.colors.surfaceInset }}>
                        {(lazyFilterOptions.people || []).length > 0 && (
                          <FilterSection title="PEOPLE CURRENTLY WORKING THERE">
                            {(lazyFilterOptions.people || []).map((f) => (
                              <FilterChip
                                key={f.key}
                                active={activeFilters.some((x) => x.key === f.key)}
                                onClick={() => toggleFilter(f)}
                                label={resolvingFilterKey === f.key ? 'Checking...' : f.label}
                                isDark={isDark}
                                border={border}
                                accentColor={accentColor}
                              />
                            ))}
                          </FilterSection>
                        )}
                      </div>
                    )}
                  </section>
                  <p style={{ fontSize: 14, color: muted, margin: '0 0 10px' }}>
                    {filteredJobs.length} shown{feedTotal ? ` of ${feedTotal}` : ''}
                  </p>
                </div>
                {!loading && (jobs.length === 0 || (jobs.length > 0 && filteredJobs.length === 0)) && (
                  <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                    <p style={{ color: muted, marginBottom: 16 }}>{noFilterDataMessage}</p>
                    {(activeFilters.length > 0 || selectedLocations.length > 0 || submittedSearch || !profileMatchOnly) && (
                      <button
                        onClick={() => {
                          setActiveFilters([]);
                          setSelectedLocations([]);
                          setSubmittedSearch('');
                          setSearch('');
                          setProfileMatchOnly(true);
                        }}
                        style={{
                          padding: '10px 20px',
                          borderRadius: tokens.radius.pill,
                          background: 'rgba(37, 99, 235, 0.08)',
                          border: `1px solid rgba(37, 99, 235, 0.2)`,
                          color: tokens.colors.accent,
                          cursor: 'pointer',
                          fontWeight: 700,
                          fontSize: 13.5,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 8,
                          transition: 'all 0.2s ease',
                          boxShadow: '0 2px 8px rgba(37,99,235,0.04)'
                        }}
                        onMouseEnter={e => {
                          e.currentTarget.style.background = 'rgba(37, 99, 235, 0.12)';
                          e.currentTarget.style.borderColor = 'rgba(37, 99, 235, 0.3)';
                          e.currentTarget.style.transform = 'translateY(-1px)';
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.background = 'rgba(37, 99, 235, 0.08)';
                          e.currentTarget.style.borderColor = 'rgba(37, 99, 235, 0.2)';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }}
                      >
                        <Filter size={16} />
                        Reset All Filters
                      </button>
                    )}
                  </div>
                )}
                <div style={{ position: 'relative' }}>
                  {loading && (
                    <div style={{
                      position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                      zIndex: 10, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: 80
                    }}>
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '12px 24px',
                        background: isDark ? '#1a1d2e' : tokens.colors.surface,
                        borderRadius: tokens.radius.pill,
                        boxShadow: isDark ? '0 8px 30px rgba(0,0,0,0.5)' : tokens.shadow.raised,
                        color: accentColor, fontWeight: 700, fontSize: 14,
                        border: `1px solid ${border}`,
                      }}>
                        <Loader2 size={18} className="spin" /> Loading jobs...
                      </div>
                    </div>
                  )}
                  <div style={{
                    transition: 'all 0.3s',
                    filter: loading || higherMatchesLoading ? 'blur(3px)' : 'none',
                    opacity: loading || higherMatchesLoading ? 0.6 : 1,
                    pointerEvents: loading || higherMatchesLoading ? 'none' : 'auto'
                  }}>
                    {filteredJobs.map((job, jobIndex) => {
                      const key = jobApplyKey(job) || `${job.company_name}-${job.role}`;
                      const isExpanded = expandedJobKey === key;
                      const isSaved = job.user_status === 'saved';

                      let whyReasons = [];
                      if (isExpanded) {
                        const fetchedReasons = reasonsMap[key] || [];
                        const rawReasons = fetchedReasons.length > 0 ? fetchedReasons : (job.reasons || []);
                        whyReasons = rawReasons.filter((r) => r.reason_type !== 'alumni_inside');
                      }
                      const maxReasons = 2;
                      const showAllReasons = !!showAllReasonsMap[key];
                      const displayReasons = showAllReasons ? whyReasons : whyReasons.slice(0, maxReasons);
                      const remainingCount = whyReasons.length - maxReasons;

                      return (
                        <React.Fragment key={key}>
                          <article className="jobs-feed-card" style={{ background: cardBg, border: `1px solid ${job.is_match ? (isDark ? 'rgba(245,158,11,0.3)' : 'rgba(37,99,235,0.22)') : border}`, borderRadius: tokens.radius.card, padding: '14px 16px', marginBottom: 12, boxShadow: isDark ? 'none' : tokens.shadow.card }}>
                            <div className="jobs-feed-card-layout" style={{ display: 'flex', gap: 12 }}>
                              <div className="jobs-feed-card-logo" style={{
                                width: 44, height: 44, borderRadius: 12, flexShrink: 0,
                                background: isDark ? 'rgba(245,158,11,0.1)' : 'rgba(37,99,235,0.08)',
                                color: accentColor,
                                border: `1px solid ${isDark ? 'rgba(245,158,11,0.2)' : 'rgba(37,99,235,0.15)'}`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontWeight: 800, fontSize: 14,
                              }}>
                                {companyInitials(job.company_name)}
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div className="jobs-feed-card-head" style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                                  <div className="jobs-feed-card-copy">
                                    <h3 style={{ margin: 0, fontSize: 15.5, fontWeight: 700, color: text, lineHeight: 1.24 }}>{job.role}</h3>
                                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, margin: '6px 0 0', fontSize: 13, color: isDark ? 'rgba(255,255,255,0.65)' : tokens.colors.textSecondary, fontWeight: 500 }}>
                                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Building2 size={13} color={muted} />{job.company_name}</span>
                                      {job.location && (
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                          <MapPin size={13} color={muted} /> {job.location}
                                        </span>
                                      )}
                                      {job.job_function && job.job_function !== 'Not Applicable' && (
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5, color: muted }}>
                                          <Layers size={13} color={muted} /> {job.job_function}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="jobs-feed-card-tools" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 28 }}>
                                      {job.is_match && (
                                        <span style={{
                                          display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px',
                                          borderRadius: tokens.radius.pill,
                                          background: isDark ? 'rgba(245,158,11,0.12)' : 'rgba(37,99,235,0.08)',
                                          border: `1px solid ${isDark ? 'rgba(245,158,11,0.25)' : 'rgba(37,99,235,0.2)'}`,
                                          fontSize: 10.5, fontWeight: 700, color: accentColor, whiteSpace: 'nowrap',
                                        }}>
                                          <Check size={12} /> Matched
                                        </span>
                                      )}
                                      <button type="button" onClick={() => toggleSaved(job)} style={{ width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', background: 'transparent', cursor: 'pointer', padding: 0 }} aria-label="Save job">
                                        <Bookmark size={18} color={isSaved ? green : muted} fill={isSaved ? green : 'none'} />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => rejectJob(job)}
                                        style={{
                                          width: 28, height: 28, borderRadius: '50%',
                                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                                          border: 'none', background: 'transparent', cursor: 'pointer',
                                          transition: 'all 0.2s',
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.07)' : tokens.colors.surfaceInset}
                                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                        aria-label="Dismiss job"
                                      >
                                        <X size={14} color={muted} strokeWidth={2.5} />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                                <div className="jobs-feed-card-tags" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginTop: 14, fontSize: 11, color: muted }}>
                                  {job.salary && (
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: accentColor, fontWeight: 600, padding: '3px 8px', borderRadius: tokens.radius.pill, background: isDark ? 'rgba(245,158,11,0.1)' : 'rgba(37,99,235,0.08)', border: `1px solid ${isDark ? 'rgba(245,158,11,0.2)' : 'rgba(37,99,235,0.12)'}` }}><IndianRupee size={13} /> {job.salary}</span>
                                  )}
                                  {formatExperience(job.experience) && (
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontWeight: 500, padding: '3px 8px', borderRadius: tokens.radius.pill, background: isDark ? 'rgba(255,255,255,0.06)' : tokens.colors.surfaceInset, border: `1px solid ${border}` }}><Briefcase size={13} /> {formatExperience(job.experience)}</span>
                                  )}
                                  {job.employment_type && (
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontWeight: 500, padding: '3px 8px', borderRadius: tokens.radius.pill, background: isDark ? 'rgba(255,255,255,0.06)' : tokens.colors.surfaceInset, border: `1px solid ${border}` }}><Layers size={13} /> {job.employment_type}</span>
                                  )}
                                  {formatPostedAgo(job.posted_at) && (
                                    <span
                                      style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 5,
                                        fontWeight: 500,
                                        padding: '3px 8px',
                                        borderRadius: tokens.radius.pill,
                                        background: isDark ? 'rgba(255,255,255,0.06)' : tokens.colors.surfaceInset,
                                        border: `1px solid ${border}`,
                                        color: muted,
                                      }}
                                    >
                                      <Clock3 size={12} />
                                      {formatPostedAgo(job.posted_at)}
                                    </span>
                                  )}
                                  {job.applicants_count != null && (
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontWeight: 500, padding: '3px 8px', borderRadius: tokens.radius.pill, background: isDark ? 'rgba(255,255,255,0.06)' : tokens.colors.surfaceInset, border: `1px solid ${border}` }}><Users size={13} /> {job.applicants_count} applicants</span>
                                  )}
                                </div>
                                <div className="jobs-feed-card-footer" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 18 }}>
                                  <JobCardActions
                                    job={job}
                                    onApplyClick={handleApplyClick}
                                    onFindContact={() => openFindContact(job)}
                                    onViewDetails={() => openViewDetails(job)}
                                    green={green}
                                    isDark={isDark}
                                    border={border}
                                    cardBg={cardBg}
                                    muted={muted}
                                    accentColor={accentColor}
                                  />
                                  {shouldShowWhyMatches && (
                                    <button
                                      type="button"
                                      onClick={async () => {
                                        const willExpand = expandedJobKey !== key;
                                        setExpandedJobKey(willExpand ? key : null);
                                        if (willExpand && !reasonsMap[key] && job.org_id) {
                                          try {
                                            setReasonsLoadingMap((prev) => ({ ...prev, [key]: true }));
                                            const data = await getOrgMatchReasons(job.org_id);
                                            setReasonsMap(prev => ({ ...prev, [key]: data.reasons || [] }));
                                          } catch (e) {
                                            console.error("Failed to fetch match reasons", e);
                                            setReasonsMap(prev => ({ ...prev, [key]: [] }));
                                          } finally {
                                            setReasonsLoadingMap((prev) => ({ ...prev, [key]: false }));
                                          }
                                        }
                                      }}
                                      style={{ border: 'none', background: 'transparent', color: muted, fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                                    >
                                      Why this matches {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                    </button>
                                  )}
                                </div>
                                {shouldShowWhyMatches && isExpanded && (
                                  <div style={{ marginTop: 12, padding: 14, borderRadius: tokens.radius.lg, background: isDark ? 'rgba(255,255,255,0.03)' : tokens.colors.surfaceInset, border: `1px solid ${border}` }}>
                                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: isDark ? 'rgba(255,255,255,0.28)' : tokens.colors.textDim, marginBottom: 4 }}>WHY THIS COMPANY</div>
                                    {reasonsLoadingMap[key] ? (
                                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: accentColor, fontSize: 13, fontWeight: 600, marginTop: 6 }}>
                                        <Loader2 size={14} className="spin" /> Loading match reasons...
                                      </div>
                                    ) : displayReasons.length > 0 ? (
                                      <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                                        {displayReasons.map((r, i) => (
                                          <li key={`${r.text}-${i}`} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 8, fontSize: 13, color: text }}>
                                            <Check size={14} color={green} style={{ marginTop: 2, flexShrink: 0 }} />
                                            {r.text}
                                          </li>
                                        ))}
                                        {remainingCount > 0 && !showAllReasons && (
                                          <li style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 8, fontSize: 12 }}>
                                            <span style={{ width: 14, display: 'inline-block' }} />
                                            <button
                                              type="button"
                                              onClick={() => setShowAllReasonsMap((prev) => ({ ...prev, [key]: true }))}
                                              style={{
                                                border: 'none',
                                                background: 'transparent',
                                                padding: 0,
                                                color: accentColor,
                                                fontSize: 12,
                                                fontWeight: 700,
                                                cursor: 'pointer',
                                              }}
                                            >
                                              + {remainingCount} more
                                            </button>
                                          </li>
                                        )}
                                        {showAllReasons && whyReasons.length > maxReasons && (
                                          <li style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 8, fontSize: 12 }}>
                                            <span style={{ width: 14, display: 'inline-block' }} />
                                            <button
                                              type="button"
                                              onClick={() => setShowAllReasonsMap((prev) => ({ ...prev, [key]: false }))}
                                              style={{
                                                border: 'none',
                                                background: 'transparent',
                                                padding: 0,
                                                color: muted,
                                                fontSize: 12,
                                                fontWeight: 700,
                                                cursor: 'pointer',
                                              }}
                                            >
                                              Show less
                                            </button>
                                          </li>
                                        )}
                                      </ul>
                                    ) : (
                                      <div style={{ marginTop: 6, fontSize: 13, color: muted }}>
                                        Your profile matches above 60% of the required qualifications
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </article>
                          {/* "Get higher-chance matches" CTA after 3rd card */}
                          {jobIndex === 2 && !higherMatchesDismissed && !higherMatchesClicked && filteredJobs.length > 3 && (
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              margin: '4px 0 12px',
                              position: 'relative',
                            }}>
                              <div style={{
                                position: 'absolute',
                                left: 0,
                                right: 0,
                                top: '50%',
                                height: 1,
                                background: `linear-gradient(90deg, transparent, ${tokens.colors.border} 30%, ${tokens.colors.border} 70%, transparent)`,
                              }} />
                              <button
                                type="button"
                                onClick={handleHigherMatchesClick}
                                style={{
                                  position: 'relative',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 8,
                                  padding: '9px 18px',
                                  borderRadius: 999,
                                  border: `1.5px solid ${accentColor}`,
                                  background: isDark ? 'rgba(245,158,11,0.06)' : tokens.colors.surface,
                                  color: accentColor,
                                  fontSize: 13.5,
                                  fontWeight: 700,
                                  cursor: 'pointer',
                                  boxShadow: '0 2px 12px rgba(37,99,235,0.10)',
                                  letterSpacing: '-0.01em',
                                  transition: 'all 0.18s ease',
                                }}
                                onMouseEnter={e => {
                                  e.currentTarget.style.background = 'rgba(37,99,235,0.06)';
                                  e.currentTarget.style.boxShadow = '0 4px 20px rgba(37,99,235,0.18)';
                                }}
                                onMouseLeave={e => {
                                  e.currentTarget.style.background = isDark ? 'rgba(245,158,11,0.06)' : tokens.colors.surface;
                                  e.currentTarget.style.boxShadow = '0 2px 12px rgba(37,99,235,0.10)';
                                }}
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M12 2L2 7l10 5 10-5-10-5z" />
                                  <path d="M2 17l10 5 10-5" />
                                  <path d="M2 12l10 5 10-5" />
                                </svg>
                                See More Personalized Matches
                                <span style={{ fontSize: 11.5, fontWeight: 600, opacity: 0.65 }}>~2 min</span>
                              </button>
                            </div>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
                {!feedExpired && (
                  <div ref={loadMoreRef} style={{ minHeight: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', color: muted, fontSize: 13 }}>
                    {loadingMore ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <Loader2 size={16} className="spin" /> Loading more
                      </span>
                    ) : hasMoreJobs ? ' ' : ''}
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>
      {/* Full-page blur overlay when higher-matches loading */}
      {higherMatchesLoading && (
        <div style={{
          position: 'fixed',
          inset: 0,
          zIndex: 200,
          background: isDark ? 'rgba(5,8,20,0.65)' : 'rgba(15,23,42,0.18)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'all',
        }}>
          <div style={{
            width: '100%',
            maxWidth: 420,
            margin: '0 16px',
            borderRadius: 20,
            border: `1px solid ${border}`,
            background: isDark ? '#0f1322' : tokens.colors.surface,
            boxShadow: isDark
              ? '0 24px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.04)'
              : '0 24px 80px rgba(37,99,235,0.18)',
            padding: '28px 24px',
            textAlign: 'center',
          }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 52, height: 52, borderRadius: 14,
              background: isDark ? 'rgba(245,158,11,0.12)' : 'rgba(37,99,235,0.10)',
              color: accentColor, marginBottom: 14,
            }}>
              <Loader2 size={26} style={{ animation: 'spin 1s linear infinite' }} />
            </div>
            <div style={{ fontSize: 18, fontWeight: 800, color: text, marginBottom: 8 }}>
              Finding your best matches
            </div>
            <div style={{ fontSize: 13.5, lineHeight: 1.6, color: isDark ? 'rgba(255,255,255,0.55)' : tokens.colors.textSecondary, marginBottom: 16 }}>
              Analyzing your background, alumni paths, referral network and fit scoring across all jobs…
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center', marginBottom: 16 }}>
              {['Your background', 'Alumni paths', 'Referral network', 'Fit scoring'].map((label) => (
                <span key={label} style={{
                  fontSize: 11.5, fontWeight: 700, color: accentColor,
                  background: isDark ? 'rgba(245,158,11,0.1)' : 'rgba(37,99,235,0.08)',
                  border: `1px solid ${isDark ? 'rgba(245,158,11,0.2)' : 'rgba(37,99,235,0.16)'}`,
                  borderRadius: 999, padding: '4px 10px',
                }}>{label}</span>
              ))}
            </div>
            <div style={{ width: '100%', height: 6, borderRadius: 999, background: isDark ? 'rgba(245,158,11,0.12)' : 'rgba(37,99,235,0.10)', overflow: 'hidden' }}>
              <div style={{
                width: '70%',
                height: '100%',
                background: isDark
                  ? 'linear-gradient(90deg, #F59E0B 0%, #D97706 100%)'
                  : 'linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)',
                animation: 'hm-progress 2.5s ease-in-out infinite alternate',
              }} />
            </div>
          </div>
        </div>
      )}
      {mobileFiltersOpen && (
        <>
          <div
            className="jobs-mobile-drawer-overlay open"
            onClick={() => setMobileFiltersOpen(false)}
            style={{
              display: 'block',
              position: 'fixed',
              inset: 0,
              zIndex: 90,
              background: 'rgba(15, 23, 42, 0.38)',
            }}
          />
          <aside
            className="jobs-mobile-filter-drawer open"
            style={{
              display: 'block',
              position: 'fixed',
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 91,
              maxHeight: 'min(80vh, 640px)',
              overflowY: 'auto',
              padding: '0 0 calc(18px + env(safe-area-inset-bottom))',
              background: tokens.colors.surface,
              borderRadius: '22px 22px 0 0',
              boxShadow: '0 -20px 60px rgba(15, 23, 42, 0.22)',
              transform: 'translateY(0)',
              pointerEvents: 'auto',
            }}
          >
            <div style={{ width: 38, height: 4, borderRadius: 99, background: tokens.colors.border, margin: '12px auto 14px' }} />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '0 18px 14px', borderBottom: `1px solid ${tokens.colors.border}`, marginBottom: 16 }}>
              <strong style={{ fontSize: 18, lineHeight: 1.15, fontWeight: 900, color: tokens.colors.textPrimary }}>Filters</strong>
              <button type="button" onClick={resetMobileFilters} style={{ border: 'none', background: 'transparent', color: tokens.colors.accent, fontSize: 13, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' }}>Reset all</button>
            </div>
            <div style={mobileSectionStyle}>
              <div style={mobileSectionLabelStyle}>Profile match</div>
              <button
                type="button"
                onClick={() => setProfileMatchOnly(!profileMatchOnly)}
                style={{ width: '100%', display: 'flex', alignItems: 'flex-start', gap: 11, textAlign: 'left', border: `1px solid ${tokens.colors.border}`, borderRadius: 14, background: tokens.colors.surfaceSubtle, padding: 12, color: tokens.colors.textPrimary, fontSize: 13, lineHeight: 1.45, cursor: 'pointer' }}
              >
                <span style={{ position: 'relative', flexShrink: 0, width: 40, height: 22, borderRadius: 999, background: profileMatchOnly ? accentColor : (isDark ? 'rgba(255,255,255,0.15)' : tokens.colors.border), marginTop: 1 }}>
                  <span style={{ position: 'absolute', top: 3, left: profileMatchOnly ? 21 : 3, width: 16, height: 16, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(15, 23, 42, 0.18)', transition: 'left 0.18s ease' }} />
                </span>
                <span>Show only <strong>{primaryRole}</strong> roles matching your profile</span>
              </button>
            </div>
            <div style={mobileSectionStyle}>
              <div style={mobileSectionLabelStyle}>Location</div>
              <div style={mobileWrapStyle}>
                <button
                  type="button"
                  onClick={() => setSelectedLocations([])}
                  style={mobileChipStyle(!selectedLocations.length)}
                >
                  All India
                </button>
                {mobileLocationOptions.map(({ loc }) => (
                  <button
                    key={loc}
                    type="button"
                    onClick={() => toggleMobileLocation(loc)}
                    style={mobileChipStyle(selectedLocations.includes(loc))}
                  >
                    {loc}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    setMobileLocationSearchOpen((v) => !v);
                    setLocSearch('');
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 36,
                    border: `1px dashed ${tokens.colors.accent}`,
                    borderRadius: 999,
                    background: 'rgba(37,99,235,0.06)',
                    color: tokens.colors.accent,
                    padding: '7px 12px',
                    fontSize: 12.5,
                    fontWeight: 850,
                    cursor: 'pointer',
                  }}
                >
                  {mobileLocationSearchOpen ? 'Close search' : '+ Add location'}
                </button>
              </div>
              {mobileLocationSearchOpen && (
                <div style={{ marginTop: 10, border: `1px solid ${tokens.colors.border}`, borderRadius: 14, background: tokens.colors.surfaceSubtle, padding: 10 }}>
                  <div style={{ position: 'relative' }}>
                    <Search size={14} color={tokens.colors.textMuted} style={{ position: 'absolute', left: 10, top: 10 }} />
                    <input
                      value={locSearch}
                      onChange={(e) => setLocSearch(e.target.value)}
                      placeholder="Search city, state, or remote..."
                      autoFocus
                      style={{
                        width: '100%',
                        minHeight: 36,
                        border: `1px solid ${tokens.colors.border}`,
                        borderRadius: 10,
                        background: tokens.colors.surface,
                        color: tokens.colors.textPrimary,
                        padding: '8px 10px 8px 32px',
                        fontSize: 13,
                        outline: 'none',
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8, maxHeight: 190, overflowY: 'auto' }}>
                    {locSearch.trim() && mobileLocationSearchResults.length === 0 && (
                      <div style={{ padding: '8px 10px', color: tokens.colors.textMuted, fontSize: 12.5 }}>
                        No matching locations found.
                      </div>
                    )}
                    {mobileLocationSearchResults.map(({ loc, count }) => {
                      const selected = selectedLocations.includes(loc);
                      return (
                        <button
                          key={loc}
                          type="button"
                          onClick={() => toggleMobileLocation(loc)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 10,
                            width: '100%',
                            border: `1px solid ${selected ? tokens.colors.accent : tokens.colors.border}`,
                            borderRadius: 10,
                            background: selected ? 'rgba(37,99,235,0.08)' : tokens.colors.surface,
                            color: selected ? tokens.colors.accent : tokens.colors.textPrimary,
                            padding: '9px 10px',
                            fontSize: 12.5,
                            fontWeight: 750,
                            textAlign: 'left',
                            cursor: 'pointer',
                          }}
                        >
                          <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{loc}</span>
                          <span style={{ flexShrink: 0, color: selected ? tokens.colors.accent : tokens.colors.textMuted, fontSize: 11, fontWeight: 850 }}>
                            {selected ? 'Selected' : `${count} jobs`}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <div style={mobileSectionStyle}>
              <div style={mobileSectionLabelStyle}>Sort by</div>
              <div style={mobileWrapStyle}>
                {SORT_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setSortBy(opt.value)}
                    style={mobileChipStyle(sortBy === opt.value)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div style={mobileSectionStyle}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 9 }}>
                <div style={{ ...mobileSectionLabelStyle, marginBottom: 0 }}>Proven Hiring Patterns</div>
                {renderMobileInfoButton('Proven Hiring Patterns', 'See jobs at companies proven to hire people from your past companies')}
              </div>
              <div style={mobileWrapStyle}>
                {mobileCoreFilters.map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => toggleFilter(f)}
                    style={mobileChipStyle(activeFilters.some((x) => x.key === f.key), true)}
                  >
                    {resolvingFilterKey === f.key ? 'Checking...' : f.label}
                  </button>
                ))}
              </div>
            </div>
            {mobilePeopleFilters.length > 0 && (
              <div style={mobileSectionStyle}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 9 }}>
                  <div style={{ ...mobileSectionLabelStyle, marginBottom: 0 }}>Stronger Referral Chances</div>
                  {renderMobileInfoButton('Stronger Referral Chances', 'See jobs at companies where you have stronger referral chances')}
                </div>
                <div style={mobileWrapStyle}>
                  {mobilePeopleFilters.map((f) => (
                    <button
                      key={f.key}
                      type="button"
                      onClick={() => toggleFilter(f)}
                      style={mobileChipStyle(activeFilters.some((x) => x.key === f.key), true)}
                    >
                      {resolvingFilterKey === f.key ? 'Checking...' : f.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <button
              type="button"
              onClick={() => setMobileFiltersOpen(false)}
              style={{ position: 'sticky', bottom: 0, width: 'calc(100% - 36px)', minHeight: 48, margin: '4px 18px 0', border: 'none', borderRadius: 14, background: tokens.colors.accent, color: '#fff', fontSize: 14, fontWeight: 900, cursor: 'pointer', boxShadow: '0 10px 28px rgba(37, 99, 235, 0.22)' }}
            >
              Apply Filters
            </button>
          </aside>
        </>
      )}
      <CompanyPeoplePanel
        isOpen={panelCompany !== null}
        onClose={() => setPanelCompany(null)}
        sourceOrgId={panelCompany?.sourceOrgId}
        sourceCompanyName={panelCompany?.sourceCompanyName}
        destOrgId={panelCompany?.destOrgId}
        companyName={panelCompany?.companyName}
        initialHop={panelCompany?.hop || 1}
        role={panelCompany?.role || ''}
        location={panelCompany?.location || ''}
        initialOuterTab={panelCompany?.initialOuterTab || 'employees'}
        initialSelectedJobId={panelCompany?.initialSelectedJobId}
        initialSelectedJob={panelCompany?.initialSelectedJob}
        isFromJobsPage={panelCompany?.isFromJobsPage}
      />
      <DidYouApplyModal
        isOpen={showApplyModal}
        onClose={() => {
          setShowApplyModal(false);
          setPendingApplyJob(null);
        }}
        onConfirm={handleApplyConfirm}
        jobTitle={pendingApplyJob?.role}
        companyName={pendingApplyJob?.companyName}
      />
      <style>{`
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes hm-progress {
          0% { width: 18%; opacity: 0.85; }
          100% { width: 88%; opacity: 1; }
        }
        .jobs-mobile-drawer-overlay,
        .jobs-mobile-filter-drawer {
          display: none;
        }
        .jobs-mobile-drawer-handle {
          width: 38px;
          height: 4px;
          border-radius: 99px;
          background: ${tokens.colors.border};
          margin: 12px auto 14px;
        }
        .jobs-mobile-drawer-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 0 18px 14px;
          border-bottom: 1px solid ${tokens.colors.border};
          margin-bottom: 16px;
        }
        .jobs-mobile-drawer-header strong {
          font-size: 18px;
          line-height: 1.15;
          font-weight: 900;
          color: ${tokens.colors.textPrimary};
        }
        .jobs-mobile-drawer-header button {
          border: none;
          background: transparent;
          color: ${tokens.colors.accent};
          font-size: 13px;
          font-weight: 800;
          cursor: pointer;
          white-space: nowrap;
        }
        .jobs-mobile-drawer-section {
          padding: 0 18px;
          margin-bottom: 18px;
        }
        .jobs-mobile-drawer-label {
          margin-bottom: 9px;
          color: ${tokens.colors.textMuted};
          font-size: 10px;
          font-weight: 900;
          letter-spacing: 0.09em;
          text-transform: uppercase;
        }
        .jobs-mobile-toggle-row {
          width: 100%;
          display: flex;
          align-items: flex-start;
          gap: 11px;
          text-align: left;
          border: 1px solid ${tokens.colors.border};
          border-radius: 14px;
          background: ${tokens.colors.surfaceSubtle};
          padding: 12px;
          color: ${tokens.colors.textPrimary};
          font-size: 13px;
          line-height: 1.45;
          cursor: pointer;
        }
        .jobs-mobile-toggle {
          position: relative;
          flex-shrink: 0;
          width: 40px;
          height: 22px;
          border-radius: 999px;
          background: ${tokens.colors.border};
          margin-top: 1px;
        }
        .jobs-mobile-toggle.on {
          background: ${tokens.colors.accent};
        }
        .jobs-mobile-toggle span {
          position: absolute;
          top: 3px;
          left: 3px;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #fff;
          box-shadow: 0 1px 3px rgba(15, 23, 42, 0.18);
          transition: transform 0.18s ease;
        }
        .jobs-mobile-toggle.on span {
          transform: translateX(18px);
        }
        .jobs-mobile-pill-wrap,
        .jobs-mobile-chip-wrap {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          align-items: center;
        }
        .jobs-mobile-pill-wrap button,
        .jobs-mobile-chip-wrap button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 36px;
          max-width: 100%;
          border: 1px solid ${tokens.colors.border};
          border-radius: 999px;
          background: ${tokens.colors.surfaceSubtle};
          color: ${tokens.colors.textPrimary};
          padding: 7px 12px;
          font-size: 12.5px;
          line-height: 1.25;
          font-weight: 750;
          cursor: pointer;
          white-space: normal;
          overflow-wrap: anywhere;
        }
        .jobs-mobile-pill-wrap button.selected,
        .jobs-mobile-chip-wrap button.selected {
          border-color: ${tokens.colors.accent};
          background: ${tokens.colors.accent};
          color: #fff;
        }
        .jobs-mobile-chip-wrap button {
          justify-content: flex-start;
          text-align: left;
        }
        .jobs-mobile-drawer-apply {
          position: sticky;
          bottom: 0;
          width: calc(100% - 36px);
          min-height: 48px;
          margin: 4px 18px 0;
          border: none;
          border-radius: 14px;
          background: ${tokens.colors.accent};
          color: #fff;
          font-size: 14px;
          font-weight: 900;
          cursor: pointer;
          box-shadow: 0 10px 28px rgba(37, 99, 235, 0.22);
        }
        .jobs-mobile-layout .jobs-main-scroll {
          width: 100% !important;
          height: calc(100vh - ${HEADER_HEIGHT}px - 74px - env(safe-area-inset-bottom)) !important;
          padding-bottom: calc(74px + env(safe-area-inset-bottom)) !important;
        }
        .jobs-mobile-layout .jobs-shell-content { padding: 14px 12px 96px !important; }
        .jobs-mobile-layout .jobs-hero-section { margin-bottom: 14px !important; }
        .jobs-mobile-layout .jobs-hero-label {
          font-size: 10px !important;
          letter-spacing: 0.1em !important;
          margin-bottom: 4px !important;
        }
        .jobs-mobile-layout .jobs-hero-subtitle {
          font-size: 12px !important;
          line-height: 1.45 !important;
          margin-top: 4px !important;
        }
        .jobs-mobile-layout .jobs-role-badge-wrap {
          width: 100% !important;
          margin-top: -4px !important;
        }
        .jobs-mobile-layout .jobs-role-badge-wrap > div {
          padding: 5px 12px !important;
          border-radius: 999px !important;
        }
        .jobs-mobile-layout .jobs-filter-row {
          display: grid !important;
          grid-template-columns: minmax(0, 1fr) auto !important;
          gap: 8px !important;
          margin-bottom: 8px !important;
        }
        .jobs-mobile-layout .jobs-filter-row > div:first-child { width: 100% !important; }
        .jobs-mobile-layout .jobs-desktop-filter-controls,
        .jobs-mobile-layout .jobs-profile-match-row,
        .jobs-mobile-layout .jobs-company-filter-label,
        .jobs-mobile-layout .jobs-company-filter-chips {
          display: none !important;
        }
        .jobs-mobile-layout .jobs-mobile-filter-button {
          display: inline-flex !important;
        }
        .jobs-mobile-layout .jobs-mobile-active-filters {
          gap: 6px !important;
          overflow-x: auto;
          padding-bottom: 4px;
          margin-bottom: 8px;
          scrollbar-width: none;
        }
        .jobs-mobile-layout .jobs-mobile-active-filters::-webkit-scrollbar { display: none; }
        .jobs-mobile-layout .jobs-mobile-active-filters:not(:empty) {
          display: flex !important;
        }
        .jobs-mobile-layout .jobs-mobile-active-filters button {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          flex-shrink: 0;
          border: none;
          border-radius: 999px;
          background: ${tokens.colors.accent};
          color: #fff;
          padding: 5px 10px;
          font-size: 11px;
          font-weight: 800;
          cursor: pointer;
        }
        .jobs-mobile-layout .jobs-search-input { padding-right: 16px !important; }
        .jobs-mobile-layout .jobs-search-hint { display: none !important; }
        .jobs-mobile-layout .jobs-shell-content h1 { font-size: 22px !important; line-height: 1.22 !important; }
        .jobs-mobile-layout .jobs-mobile-drawer-overlay {
          position: fixed;
          inset: 0;
          z-index: 90;
          background: rgba(15, 23, 42, 0.38);
        }
        .jobs-mobile-layout .jobs-mobile-filter-drawer {
          position: fixed;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 91;
          max-height: min(80vh, 640px);
          overflow-y: auto;
          padding: 0 0 calc(18px + env(safe-area-inset-bottom));
          background: ${tokens.colors.surface};
          border-radius: 22px 22px 0 0;
          box-shadow: 0 -20px 60px rgba(15, 23, 42, 0.22);
          transition: transform 0.24s ease;
        }
        .jobs-mobile-layout .jobs-mobile-drawer-handle {
          width: 38px;
          height: 4px;
          border-radius: 99px;
          background: ${tokens.colors.border};
          margin: 12px auto 16px;
        }
        .jobs-mobile-layout .jobs-mobile-drawer-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 16px 12px;
          border-bottom: 1px solid ${tokens.colors.border};
          margin-bottom: 16px;
        }
        .jobs-mobile-layout .jobs-mobile-drawer-header strong {
          font-size: 16px;
          color: ${tokens.colors.textPrimary};
        }
        .jobs-mobile-layout .jobs-mobile-drawer-header button {
          border: none;
          background: transparent;
          color: ${tokens.colors.accent};
          font-size: 12px;
          font-weight: 800;
          cursor: pointer;
        }
        .jobs-mobile-layout .jobs-mobile-drawer-section {
          padding: 0 16px;
          margin-bottom: 18px;
        }
        .jobs-mobile-layout .jobs-mobile-drawer-label {
          margin-bottom: 8px;
          color: ${tokens.colors.textMuted};
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }
        .jobs-mobile-layout .jobs-mobile-toggle-row {
          width: 100%;
          display: flex;
          align-items: flex-start;
          gap: 10px;
          text-align: left;
          border: none;
          border-radius: 12px;
          background: ${tokens.colors.surfaceSubtle};
          padding: 12px;
          color: ${tokens.colors.textPrimary};
          font-size: 12px;
          line-height: 1.45;
          cursor: pointer;
        }
        .jobs-mobile-layout .jobs-mobile-toggle {
          position: relative;
          flex-shrink: 0;
          width: 38px;
          height: 21px;
          border-radius: 999px;
          background: ${tokens.colors.border};
          margin-top: 1px;
        }
        .jobs-mobile-layout .jobs-mobile-toggle.on {
          background: ${tokens.colors.accent};
        }
        .jobs-mobile-layout .jobs-mobile-toggle span {
          position: absolute;
          top: 3px;
          left: 3px;
          width: 15px;
          height: 15px;
          border-radius: 50%;
          background: #fff;
          box-shadow: 0 1px 3px rgba(15, 23, 42, 0.18);
          transition: transform 0.18s ease;
        }
        .jobs-mobile-layout .jobs-mobile-toggle.on span {
          transform: translateX(17px);
        }
        .jobs-mobile-layout .jobs-mobile-pill-wrap,
        .jobs-mobile-layout .jobs-mobile-chip-wrap {
          display: flex;
          flex-wrap: wrap;
          gap: 7px;
        }
        .jobs-mobile-layout .jobs-mobile-pill-wrap button,
        .jobs-mobile-layout .jobs-mobile-chip-wrap button {
          border: 1px solid ${tokens.colors.border};
          border-radius: 999px;
          background: ${tokens.colors.surfaceSubtle};
          color: ${tokens.colors.textPrimary};
          padding: 7px 12px;
          font-size: 12px;
          font-weight: 700;
          cursor: pointer;
        }
        .jobs-mobile-layout .jobs-mobile-pill-wrap button.selected,
        .jobs-mobile-layout .jobs-mobile-chip-wrap button.selected {
          border-color: ${tokens.colors.accent};
          background: ${tokens.colors.accent};
          color: #fff;
        }
        .jobs-mobile-layout .jobs-mobile-drawer-apply {
          width: calc(100% - 32px);
          min-height: 46px;
          margin: 0 16px;
          border: none;
          border-radius: 14px;
          background: ${tokens.colors.accent};
          color: #fff;
          font-size: 14px;
          font-weight: 900;
          cursor: pointer;
        }
        .jobs-mobile-layout .jobs-feed-card {
          border-radius: 18px !important;
          padding: 14px !important;
          margin-bottom: 12px !important;
        }
        .jobs-mobile-layout .jobs-feed-card-layout { gap: 10px !important; }
        .jobs-mobile-layout .jobs-feed-card-logo {
          width: 40px !important;
          height: 40px !important;
          border-radius: 12px !important;
          font-size: 13px !important;
        }
        .jobs-mobile-layout .jobs-feed-card-head {
          align-items: flex-start !important;
          gap: 10px !important;
        }
        .jobs-mobile-layout .jobs-feed-card-copy h3 {
          font-size: 15px !important;
          overflow-wrap: anywhere;
        }
        .jobs-mobile-layout .jobs-feed-card-tools {
          gap: 4px !important;
          min-width: 86px;
        }
        .jobs-mobile-layout .jobs-feed-card-tools > div {
          gap: 4px !important;
          flex-wrap: wrap !important;
          justify-content: flex-end !important;
        }
        .jobs-mobile-layout .jobs-feed-card-tools span { max-width: 96px; }
        .jobs-mobile-layout .jobs-feed-card-tags {
          margin-top: 12px !important;
          gap: 6px !important;
        }
        .jobs-mobile-layout .jobs-feed-card-footer {
          align-items: stretch !important;
          gap: 10px !important;
          margin-top: 14px !important;
        }
        .jobs-mobile-layout .jobs-card-actions {
          display: grid !important;
          grid-template-columns: minmax(92px, 0.92fr) minmax(122px, 1.15fr) minmax(88px, 0.9fr);
          gap: 8px !important;
          width: 100%;
        }
        .jobs-mobile-layout .jobs-card-actions > * {
          justify-content: center !important;
          min-height: 40px !important;
          padding-left: 8px !important;
          padding-right: 8px !important;
          white-space: nowrap;
        }
        .jobs-mobile-layout .jobs-feed-card-footer > button {
          width: 100%;
          justify-content: flex-start !important;
          padding-top: 2px !important;
          color: ${tokens.colors.accent} !important;
        }
        @media (max-width: 720px), (max-device-width: 720px) {
          .app-header-brand { display: inline !important; }
          .jobs-main-scroll {
            width: 100% !important;
            height: calc(100vh - ${HEADER_HEIGHT}px - 74px - env(safe-area-inset-bottom)) !important;
            padding-bottom: calc(74px + env(safe-area-inset-bottom)) !important;
          }
          .jobs-shell-content { padding: 14px 12px 96px !important; }
          .jobs-hero-section { margin-bottom: 14px !important; }
          .jobs-hero-label {
            font-size: 10px !important;
            letter-spacing: 0.1em !important;
            margin-bottom: 4px !important;
          }
          .jobs-hero-subtitle {
            font-size: 12px !important;
            line-height: 1.45 !important;
            margin-top: 4px !important;
          }
          .jobs-role-badge-wrap {
            width: 100% !important;
            margin-top: -4px !important;
          }
          .jobs-role-badge-wrap > div {
            padding: 5px 12px !important;
            border-radius: 999px !important;
          }
          .jobs-filter-row {
            display: grid !important;
            grid-template-columns: minmax(0, 1fr) auto !important;
            gap: 8px !important;
            margin-bottom: 8px !important;
          }
          .jobs-filter-row > div:first-child { width: 100% !important; }
          .jobs-desktop-filter-controls,
          .jobs-profile-match-row,
          .jobs-company-filter-label,
          .jobs-company-filter-chips {
            display: none !important;
          }
          .jobs-mobile-filter-button {
            display: inline-flex !important;
          }
          .jobs-mobile-active-filters {
            gap: 6px !important;
            overflow-x: auto;
            padding-bottom: 4px;
            margin-bottom: 8px;
            scrollbar-width: none;
          }
          .jobs-mobile-active-filters::-webkit-scrollbar { display: none; }
          .jobs-mobile-active-filters:not(:empty) {
            display: flex !important;
          }
          .jobs-mobile-active-filters button {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            flex-shrink: 0;
            border: none;
            border-radius: 999px;
            background: ${tokens.colors.accent};
            color: #fff;
            padding: 5px 10px;
            font-size: 11px;
            font-weight: 800;
            cursor: pointer;
          }
          .jobs-search-input { padding-right: 16px !important; }
          .jobs-search-hint { display: none !important; }
          .jobs-shell-content h1 { font-size: 22px !important; line-height: 1.22 !important; }
          .jobs-mobile-drawer-overlay {
            position: fixed;
            inset: 0;
            z-index: 90;
            background: rgba(15, 23, 42, 0.38);
          }
          .jobs-mobile-drawer-overlay.open {
            display: block;
          }
          .jobs-mobile-filter-drawer {
            position: fixed;
            left: 0;
            right: 0;
            bottom: 0;
            z-index: 91;
            display: block;
            max-height: min(80vh, 640px);
            overflow-y: auto;
            padding: 0 0 calc(18px + env(safe-area-inset-bottom));
            background: ${tokens.colors.surface};
            border-radius: 22px 22px 0 0;
            box-shadow: 0 -20px 60px rgba(15, 23, 42, 0.22);
            transform: translateY(105%);
            transition: transform 0.24s ease;
            pointer-events: none;
          }
          .jobs-mobile-filter-drawer.open {
            transform: translateY(0);
            pointer-events: auto;
          }
          .jobs-mobile-drawer-handle {
            width: 38px;
            height: 4px;
            border-radius: 99px;
            background: ${tokens.colors.border};
            margin: 12px auto 16px;
          }
          .jobs-mobile-drawer-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 0 16px 12px;
            border-bottom: 1px solid ${tokens.colors.border};
            margin-bottom: 16px;
          }
          .jobs-mobile-drawer-header strong {
            font-size: 16px;
            color: ${tokens.colors.textPrimary};
          }
          .jobs-mobile-drawer-header button {
            border: none;
            background: transparent;
            color: ${tokens.colors.accent};
            font-size: 12px;
            font-weight: 800;
            cursor: pointer;
          }
          .jobs-mobile-drawer-section {
            padding: 0 16px;
            margin-bottom: 18px;
          }
          .jobs-mobile-drawer-label {
            margin-bottom: 8px;
            color: ${tokens.colors.textMuted};
            font-size: 10px;
            font-weight: 800;
            letter-spacing: 0.08em;
            text-transform: uppercase;
          }
          .jobs-mobile-toggle-row {
            width: 100%;
            display: flex;
            align-items: flex-start;
            gap: 10px;
            text-align: left;
            border: none;
            border-radius: 12px;
            background: ${tokens.colors.surfaceSubtle};
            padding: 12px;
            color: ${tokens.colors.textPrimary};
            font-size: 12px;
            line-height: 1.45;
            cursor: pointer;
          }
          .jobs-mobile-toggle {
            position: relative;
            flex-shrink: 0;
            width: 38px;
            height: 21px;
            border-radius: 999px;
            background: ${tokens.colors.border};
            margin-top: 1px;
          }
          .jobs-mobile-toggle.on {
            background: ${tokens.colors.accent};
          }
          .jobs-mobile-toggle span {
            position: absolute;
            top: 3px;
            left: 3px;
            width: 15px;
            height: 15px;
            border-radius: 50%;
            background: #fff;
            box-shadow: 0 1px 3px rgba(15, 23, 42, 0.18);
            transition: transform 0.18s ease;
          }
          .jobs-mobile-toggle.on span {
            transform: translateX(17px);
          }
          .jobs-mobile-pill-wrap,
          .jobs-mobile-chip-wrap {
            display: flex;
            flex-wrap: wrap;
            gap: 7px;
          }
          .jobs-mobile-pill-wrap button,
          .jobs-mobile-chip-wrap button {
            border: 1px solid ${tokens.colors.border};
            border-radius: 999px;
            background: ${tokens.colors.surfaceSubtle};
            color: ${tokens.colors.textPrimary};
            padding: 7px 12px;
            font-size: 12px;
            font-weight: 700;
            cursor: pointer;
          }
          .jobs-mobile-pill-wrap button.selected,
          .jobs-mobile-chip-wrap button.selected {
            border-color: ${tokens.colors.accent};
            background: ${tokens.colors.accent};
            color: #fff;
          }
          .jobs-mobile-drawer-apply {
            width: calc(100% - 32px);
            min-height: 46px;
            margin: 0 16px;
            border: none;
            border-radius: 14px;
            background: ${tokens.colors.accent};
            color: #fff;
            font-size: 14px;
            font-weight: 900;
            cursor: pointer;
          }
          .jobs-feed-card {
            border-radius: 18px !important;
            padding: 14px !important;
            margin-bottom: 12px !important;
          }
          .jobs-feed-card-layout {
            gap: 10px !important;
          }
          .jobs-feed-card-logo {
            width: 40px !important;
            height: 40px !important;
            border-radius: 12px !important;
            font-size: 13px !important;
          }
          .jobs-feed-card-head {
            align-items: flex-start !important;
            gap: 10px !important;
          }
          .jobs-feed-card-copy h3 {
            font-size: 15px !important;
            overflow-wrap: anywhere;
          }
          .jobs-feed-card-tools {
            gap: 4px !important;
            min-width: 86px;
          }
          .jobs-feed-card-tools > div {
            gap: 4px !important;
            flex-wrap: wrap !important;
            justify-content: flex-end !important;
          }
          .jobs-feed-card-tools span {
            max-width: 96px;
          }
          .jobs-feed-card-tags {
            margin-top: 12px !important;
            gap: 6px !important;
          }
          .jobs-feed-card-footer {
            align-items: stretch !important;
            gap: 10px !important;
            margin-top: 14px !important;
          }
          .jobs-card-actions {
            display: grid !important;
            grid-template-columns: minmax(92px, 0.92fr) minmax(122px, 1.15fr) minmax(88px, 0.9fr);
            gap: 8px !important;
            width: 100%;
          }
          .jobs-card-actions > * {
            justify-content: center !important;
            min-height: 40px !important;
            padding-left: 8px !important;
            padding-right: 8px !important;
            white-space: nowrap;
          }
          .jobs-feed-card-footer > button {
            width: 100%;
            justify-content: flex-start !important;
            padding-top: 2px !important;
            color: ${tokens.colors.accent} !important;
          }
        }
        @media (max-width: 390px) {
          .jobs-card-actions {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};
const chipStyleMuted = {
  display: 'inline-block',
  fontSize: 12,
  padding: '6px 12px',
  borderRadius: tokens.radius.pill,
  border: `1px solid ${tokens.colors.border}`,
  background: tokens.colors.surface,
  color: tokens.colors.textMuted,
  marginRight: 8,
  marginBottom: 8,
};
const CounterPill = ({ icon: Icon, label, count }) => (
  <div style={{
    display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 14px',
    borderRadius: tokens.radius.pill, border: `1px solid ${tokens.colors.border}`, background: tokens.colors.surface, fontSize: 13, fontWeight: 700,
  }}>
    <Icon size={14} /> {label} {count}
  </div>
);
const StatCard = ({ icon: Icon, label, value }) => (
  <div style={{ background: tokens.colors.surface, border: `1px solid ${tokens.colors.border}`, borderRadius: tokens.radius.card, padding: 14, boxShadow: tokens.shadow.card }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: tokens.colors.textMuted, fontSize: 12, marginBottom: 6 }}>
      <Icon size={14} /> {label}
    </div>
    <div style={{ fontSize: 28, fontWeight: 800, color: tokens.colors.textPrimary }}>{value}</div>
  </div>
);
const FilterChip = ({ active, onClick, label, icon: Icon, isDark, border, accentColor }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      padding: '6px 12px', borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: 'pointer',
      border: `1px solid ${active ? (isDark ? 'rgba(245,158,11,0.35)' : 'rgba(37,99,235,0.24)') : (border || (isDark ? 'rgba(255,255,255,0.1)' : tokens.colors.border))}`,
      background: active ? (isDark ? 'rgba(245,158,11,0.12)' : 'rgba(37,99,235,0.08)') : (isDark ? 'rgba(255,255,255,0.05)' : tokens.colors.surface),
      color: active ? (accentColor || (isDark ? '#F59E0B' : tokens.colors.accent)) : (isDark ? 'rgba(255,255,255,0.8)' : tokens.colors.textPrimary),
      display: 'inline-flex', alignItems: 'center', gap: 6,
    }}
  >
    {Icon && <Icon size={14} />} {label}
  </button>
);
const FilterSection = ({ title, children }) => (
  <div style={{ marginBottom: 12 }}>
    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: tokens.colors.textDim, marginBottom: 8 }}>{title}</div>
    <div style={{ display: 'flex', flexWrap: 'wrap', columnGap: 8, rowGap: 12 }}>{children}</div>
  </div>
);
const DropdownButton = ({ label, open, onToggle, children, isDark, border, cardBg, text, noPadding }) => (
  <div style={{ position: 'relative' }}>
    <button
      type="button"
      onClick={onToggle}
      style={{
        padding: '12px 18px', borderRadius: tokens.radius.lg,
        border: `1px solid ${border || (isDark ? 'rgba(255,255,255,0.1)' : tokens.colors.border)}`,
        background: cardBg || (isDark ? 'rgba(255,255,255,0.05)' : tokens.colors.surface),
        color: text || (isDark ? 'rgba(255,255,255,0.85)' : tokens.colors.textPrimary),
        fontSize: 14, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
        display: 'flex', alignItems: 'center', gap: 10,
        height: '100%', minHeight: 46
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>{label}</div>
      <ChevronDown size={16} style={{ flexShrink: 0, opacity: 0.7 }} />
    </button>
    {open && (
      <div style={{
        position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 30,
        minWidth: 200, overflow: 'hidden',
        padding: noPadding ? 0 : 10,
        background: cardBg || (isDark ? '#0d1019' : tokens.colors.surface),
        border: `1px solid ${border || (isDark ? 'rgba(255,255,255,0.1)' : tokens.colors.border)}`,
        borderRadius: tokens.radius.md,
        boxShadow: isDark ? '0 12px 36px rgba(0,0,0,0.6)' : tokens.shadow.menu,
        color: text || (isDark ? 'rgba(255,255,255,0.85)' : tokens.colors.textPrimary),
      }}>
        {children}
      </div>
    )}
  </div>
);
const JobCardActions = ({ job, onApplyClick, onFindContact, onViewDetails, green, isDark, border, cardBg, muted, accentColor }) => (
  <div className="jobs-card-actions" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
    {job.job_url && (
      <a
        href={job.job_url}
        target="_blank"
        rel="noreferrer"
        onClick={() => onApplyClick?.(job)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px',
          borderRadius: tokens.radius.md,
          background: isDark ? 'linear-gradient(135deg,#F59E0B,#D97706)' : tokens.colors.accent,
          color: isDark ? '#1a1612' : '#fff',
          fontWeight: 700, fontSize: 12, textDecoration: 'none',
        }}
      >
        <Rocket size={14} /> Apply
      </a>
    )}
    <button
      type="button"
      onClick={onFindContact}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px',
        borderRadius: tokens.radius.md,
        border: `1px solid ${isDark ? 'rgba(245,158,11,0.25)' : 'rgba(37,99,235,0.2)'}`,
        background: isDark ? 'rgba(245,158,11,0.1)' : 'rgba(37,99,235,0.08)',
        color: accentColor || (isDark ? '#F59E0B' : tokens.colors.accent),
        fontWeight: 600, fontSize: 12, cursor: 'pointer',
      }}
    >
      <UserPlus size={14} /> Find contact
    </button>
    <button
      type="button"
      onClick={onViewDetails}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px',
        borderRadius: tokens.radius.md,
        border: `1px solid ${border || (isDark ? 'rgba(255,255,255,0.08)' : tokens.colors.border)}`,
        background: isDark ? 'rgba(255,255,255,0.05)' : tokens.colors.surface,
        color: muted || (isDark ? 'rgba(255,255,255,0.5)' : tokens.colors.textMuted),
        fontWeight: 600, fontSize: 12, cursor: 'pointer',
      }}
    >
      <FileText size={14} /> View details
    </button>
  </div>
);
export default MailerJobsPage;
